import * as _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { type Dependency, type DependencyIssue, Status } from '../../../../../../shared/dependenciesV2';
import { LICENSE_CONCLUDED } from '../../../../../../shared/featureFlags/flagNames';
import Locator from '../../../../../../shared/Locator';
import { useIsFeatureEnabled } from '../../../../../helpers/features';
import * as DependenciesV2Store from '../../../../../redux/dependenciesV2';
import { addAndFetchBuildWatchRevision } from '../../../../../redux/dependenciesV2/actions';
import List from '../../../../generic/List';
import PartialResultGate from '../../../generic/PartialResult/PartialResultGate';
import type { DependencyV2Modals } from '../../hooks/useDependencyModals';

import DependencyActionDropdown from './DependencyActionDropdown';
import DependencyData from './DependencyData';
import DependencyTitle from './DependencyTitle/DependencyTitle';
import DetailsRow from './DetailsRow';
import { getIsAnalyzing, getStatusFromDependencyState, isDependencyBlocked } from './helpers';
import StateContextPanel from './StateContextPanel';

import styles from './DependencyRow.module.less';

type Props = {
  dependency: Dependency;
  modals: DependencyV2Modals;
  setSelectedDependencyRevisionLocator: (locator: string) => void;
};

const DependencyTableRow: VoidFunctionComponent<Props> = ({
  dependency,
  modals,
  setSelectedDependencyRevisionLocator,
}) => {
  const dispatch = useDispatch();
  const isProjectScope = useSelector(DependenciesV2Store.selectors.getIsProjectScope);
  const revisionInstance = useSelector(DependenciesV2Store.selectors.getBuildWatchRevision(dependency.locator));
  // Memo-ize this to prevent the Locator object from changing, triggering downstream actions
  const locator = useMemo(() => new Locator(dependency.locator), [dependency.locator]);
  const status = getStatusFromDependencyState(dependency.status);
  const isBlocked = isDependencyBlocked(dependency.issues as DependencyIssue[]);
  const isManual = dependency.isManual;
  const isOrgMember = useSelector(DependenciesV2Store.selectors.getIsOrgMember);
  const isLicenseConcludedEnabled = useIsFeatureEnabled(LICENSE_CONCLUDED);

  // Load revision build data on start if the status is not analyzed
  useEffect(() => {
    if (status !== Status.Analyzed && status !== Status.Unknown) {
      // Load for all non-analyzed revisions, but only watch revisions that are in progress
      const shouldWatch = status === Status.InProgress;
      dispatch(addAndFetchBuildWatchRevision(dependency.locator, shouldWatch));
    }
    // Only include revision, because we don't want to reload data if the status changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependency.locator]);

  // Stop watching the revision if there are no more running builds
  useEffect(() => {
    if (!revisionInstance) {
      return;
    }

    // If the dependency isn't analyzing, stop watching it. Only watch in-progress builds
    if (!getIsAnalyzing(revisionInstance?.builds ?? [])) {
      dispatch(DependenciesV2Store.actions.removeRevisionFromBuildWatch(dependency.locator));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revisionInstance]);

  const licenseConcludedDependencyTitleClick = () => {
    setSelectedDependencyRevisionLocator(dependency.locator);
  };

  // TODO: Remove this once the License Concluded feature is fully launched
  // Only show the "Edit Dep" modal if being viewed by an org member.
  const dependencyTitleOnClick = isOrgMember
    ? () => modals.editDependencyModal.onOpen({ context: { revision: dependency.locator } })
    : _.noop;

  return (
    <List.RowContainer>
      <List.Row
        hasTightPadding
        onClick={isLicenseConcludedEnabled ? licenseConcludedDependencyTitleClick : dependencyTitleOnClick}
      >
        <div className={styles.DependencyRow}>
          <div className={styles.DependencyInfo}>
            <DependencyTitle
              title={dependency.title}
              revision={locator}
              version={dependency.version}
              depth={dependency.depth}
              layerDepth={dependency.layerDepth}
              isBlocked={isBlocked}
              isManual={isManual}
              onClick={isLicenseConcludedEnabled ? licenseConcludedDependencyTitleClick : dependencyTitleOnClick}
              modals={modals}
            />
            {/* If License Concluded is enabled, the dependency data is shown in the Dependency drawer and title */}
            {!isLicenseConcludedEnabled && (
              <DependencyData dependency={dependency} modals={modals} isProjectScope={isProjectScope} />
            )}
            {status === Status.Analyzed ? (
              <DetailsRow dependency={dependency} />
            ) : (
              <PartialResultGate>
                <StateContextPanel revision={locator} status={dependency.status} modals={modals} />
              </PartialResultGate>
            )}
          </div>
          {isOrgMember && (
            <PartialResultGate>
              <div className={styles.DependencyRowPullRight}>
                <DependencyActionDropdown
                  dependency={dependency}
                  status={status}
                  modals={modals}
                  isProjectScope={isProjectScope}
                  isLicenseConcludedEnabled={isLicenseConcludedEnabled}
                />
              </div>
            </PartialResultGate>
          )}
        </div>
      </List.Row>
    </List.RowContainer>
  );
};

export default DependencyTableRow;
